import type { Data } from '@strapi/types';
import { useTranslations } from '@power-core/toolkit';
import { type FunctionComponent, useState } from 'react';

import { useAppContext } from '@/modules/app-context/use-app-context';
import { Button } from '@/ui-components/button/button';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { Input } from '@/ui-components/input/input';
import { LoadingIndicator } from '@/ui-components/loading-indicator/loading-indicator';
import { Modal } from '@/ui-components/modal/modal';

import { useCustomerApi } from '../hooks/use-customer-api';
import { NewCustomerRow } from './new-customer';

export const CustomersModal: FunctionComponent = () => {
	const t = useTranslations();
	const { customers } = useAppContext();
	const { updateCustomer, deleteCustomer, isPending } = useCustomerApi();
	const [isNew, setIsNew] = useState(false);
	const [isEdit, setIsEdit] = useState<string | null>(null);
	const [editedCustomerName, setEditedCustomerName] = useState<string>('');
	const [deleteError, setDeleteError] = useState<Record<string, string | null>>({});

	const handleEditButton = (documentId: Pick<Data.Entity<'api::customer.customer'>, 'documentId'>['documentId']) => {
		setIsEdit(documentId);
		const customer = customers?.find((c) => c.documentId === documentId);
		if (customer) {
			setEditedCustomerName(customer.name || '');
		}
	};

	const handleInputChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		documentId: Pick<Data.Entity<'api::customer.customer'>, 'documentId'>['documentId'],
	) => {
		if (documentId === isEdit) {
			setEditedCustomerName(e.target.value);
		}
	};

	const handleNewCustomerButton = () => {
		setIsNew(true);
		setIsEdit(null);
		setDeleteError({});
	};

	const handleSaveButton = (documentId: Pick<Data.Entity<'api::customer.customer'>, 'documentId'>['documentId']) => {
		updateCustomer.mutateAsync({ documentId: documentId, body: { name: editedCustomerName } }).then(() => setIsEdit(null));
	};

	const handleDeleteButton = (documentId: Pick<Data.Entity<'api::customer.customer'>, 'documentId'>['documentId']) => {
		deleteCustomer.mutateAsync({ documentId }).catch((err) => {
			setDeleteError((prevState) => ({
				...prevState,
				[documentId]: t(err.response.data.error.message),
			}));
		});
	};

	const renderLoadingIndicator = (isEditing: boolean) => {
		if (isPending && isEditing)
			return (
				<Flexbox justify='center' className='w-full'>
					<LoadingIndicator sizing='small' color='primary' />
				</Flexbox>
			);
	};

	return (
		<Modal name='Customers' title={{ text: t('T_MANAGE_CUSTOMERS'), variant: 'h5' }} dismissible closeButton className='relative'>
			<div className='absolute top-[64px] right-9'>
				<Button disabled={isNew || isPending} onClick={handleNewCustomerButton} variant='primary' sizing='small'>
					{`+ ${t('T_ADD_CUSTOMER')}`}
				</Button>
			</div>
			<div className='max-h-[230px] min-w-[500px] h-[500px] overflow-y-scroll scrollbar-rounded'>
				{customers &&
					customers?.length > 0 &&
					customers?.map((customer) => {
						const isEditing = customer.documentId === isEdit;
						return (
							<Flexbox key={customer.documentId} className='gap-4 p-0.5 items-baseline' justify='between'>
								<Input
									name={`customerName-${customer.documentId}`}
									sizing='small'
									value={isEditing ? editedCustomerName : (customer.name ?? '')}
									placeholder={t('T_ENTER_CUSTOMER_NAME')}
									containerClassname='w-64'
									disabled={isNew || (!isNew && !isEditing)}
									onChange={(e) => handleInputChange(e, customer.documentId)}
									error={deleteError[customer.documentId]}
								/>

								{renderLoadingIndicator(isEditing)}

								<Flexbox className='gap-1'>
									<Button
										variant='text'
										disabled={isNew || isEditing || isPending}
										sizing='small'
										className='font-normal text-sm w-fit text-dark dark:text-white'
										onClick={() => {
											handleEditButton(customer.documentId);
											setDeleteError({});
										}}
									>
										{t('T_EDIT')}
									</Button>
									<Button
										variant='text'
										disabled={isNew || !isEditing || isPending}
										sizing='small'
										className='font-normal text-sm w-fit text-accent'
										onClick={() => handleSaveButton(customer.documentId)}
									>
										{t('T_SAVE')}
									</Button>
									<Button
										variant='text'
										disabled={isNew || isPending}
										sizing='small'
										className='font-normal text-sm w-fit text-brand'
										onClick={isEditing ? () => setIsEdit(null) : () => handleDeleteButton(customer.documentId)}
									>
										{isEditing ? t('T_CANCEL') : t('T_DELETE')}
									</Button>
								</Flexbox>
							</Flexbox>
						);
					})}
			</div>
			{isNew && <NewCustomerRow setIsNew={setIsNew} />}
		</Modal>
	);
};
